import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('firewatch_token'));

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          apiService.setToken(token);
          const userData = await apiService.getCurrentUser();
          setUser(userData.user);
        } catch (error) {
          console.error('Auth initialization error:', error);
          // Token is invalid, clear it
          localStorage.removeItem('firewatch_token');
          setToken(null);
          apiService.setToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await apiService.login(email, password);
      
      const { access_token, user: userData } = response;
      
      // Store token
      localStorage.setItem('firewatch_token', access_token);
      setToken(access_token);
      apiService.setToken(access_token);
      
      // Set user data
      setUser(userData);
      
      toast.success('Login realizado com sucesso!', {
        duration: 3000, // 3 seconds for success messages
        position: 'top-center'
      });
      return { success: true };
      
    } catch (error) {
      console.error('Login error:', error);
      
      let message = 'Erro no login';
      
      if (error.response?.status === 401) {
        message = 'Email ou palavra-passe incorretos';
      } else if (error.response?.status === 403) {
        message = 'Conta desativada. Contacte o administrador';
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        message = 'Erro de ligação ao servidor';
      }
      
      // Don't show toast for login errors - rely on form error display instead
      // This prevents the "too fast" issue and gives users persistent feedback
      
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('firewatch_token');
    setToken(null);
    setUser(null);
    apiService.setToken(null);
    toast.success('Logout realizado com sucesso!');
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    updateUser,
    isAdmin,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
